import React from "react";
import Webcam from "react-webcam";
import { Dialog, Slide, SlideProps } from "@mui/material";
import { Button as ButtonAntd, ConfigProvider, message, Space } from "antd";
import {
  BulbTwoTone,
  CameraOutlined,
  CloseCircleOutlined,
  InteractionTwoTone,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { useRouterStore } from "../../../../libs/stores";

// Slide Transition (MUI) para el Dialog
const Transition = React.forwardRef<HTMLDivElement, SlideProps>(
  function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
  }
);

// Estilos in-line
const styles = {
  container: {
    position: "fixed" as const,
    width: "100%",
    zIndex: 1,
    background: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  closeBtn: {
    position: "absolute" as const,
    top: 1,
    right: 1,
    zIndex: 2,
  },
  torchBtn: {
    position: "absolute" as const,
    top: 1,
    left: 1,
    zIndex: 2,
  },
  flipCameraBtn: {
    position: "absolute" as const,
    bottom: 15,
    left: "calc(50vw - 87px)",
    zIndex: 2,
  },
  takePhotoBtn: {
    position: "absolute" as const,
    bottom: 10,
    left: "calc(50vw - 33px)",
    width: 70,
    height: 70,
    zIndex: 2,
  },
  zoomContainer: {
    position: "absolute" as const,
    top: 150,
    right: 2,
    zIndex: 2,
  },
  nativeCameraBtn: {
    // Nuevo estilo para el botón de cámara nativa
    position: "absolute" as const,
    bottom: 15,
    right: "calc(50vw - 93px)",
    zIndex: 2,
  },
};

interface CameraComponentProps {
  open: boolean;
  handleClose: () => void;
  setFoto: (foto: string) => void;
  upload?: boolean;
}

export const CameraComponentExporBananas: React.FC<CameraComponentProps> = ({
  open,
  handleClose,
  setFoto,
  upload=false,
}) => {
  // Refs y stores
  const webcamRef = React.useRef<Webcam>(null);
  const photoWidth = useRouterStore((state) => state.photoWidth);
  const photoHeight = useRouterStore((state) => state.photoHeight);

  // Estados
  const [loading, setLoading] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const [numberOfCameras, setNumberOfCameras] = React.useState(0);
  const [isTorchOn, setIsTorchOn] = React.useState(false);
  const [isChangingCamera, setIsChangingCamera] = React.useState(false);
  const [mirror, setMirror] = React.useState(false);
  const [torchSupported, setTorchSupported] = React.useState<boolean | null>(
    null
  ); // Nuevo estado para soporte de torch

  // Para almacenar las constraints de la cámara
  const [videoConstraints, setVideoConstraints] =
    React.useState<MediaTrackConstraints>(() => ({
      facingMode: "environment",
      width: photoWidth,
      height: photoHeight,
    }));

  // Efecto: enumerar cámaras AL abrir el modal
  React.useEffect(() => {
    if (!open) return;

    let isActive = true;
    (async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isActive) return;
        const videoDevices = devices.filter((i) => i.kind === "videoinput");
        setNumberOfCameras(videoDevices.length);
        setReady(true);
      } catch (err: any) {
        message.error("No se pudo acceder a la cámara");
        console.error(err);
      }
    })();

    return () => {
      isActive = false;
      setReady(false);
      setIsTorchOn(false);
      setIsChangingCamera(false);
      setTorchSupported(null);
    };
  }, [open]);

  const getVideoTrack = React.useCallback((): MediaStreamTrack | null => {
    const stream = webcamRef.current?.video?.srcObject as MediaStream;
    if (!stream) return null;
    const [track] = stream.getVideoTracks();
    return track || null;
  }, []);

  // Efecto: verificar soporte para torch cuando la cámara esté lista
  React.useEffect(() => {
    if (!ready || !webcamRef.current) return;

    const track = getVideoTrack();
    if (track) {
      const capabilities = track.getCapabilities() as any;
      setTorchSupported(!!capabilities.torch);
    }
  }, [ready, getVideoTrack]);

  // Función auxiliar: obtener track de video

  // Tomar foto
  const takeFoto = React.useCallback(async () => {
    try {
      if (!webcamRef.current) return;
      setLoading(true);
      const dataUrl = webcamRef.current.getScreenshot();
      if (!dataUrl) {
        setLoading(false);
        return;
      }
      setFoto(dataUrl);
      setLoading(false);
      handleClose();
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  }, [handleClose, setFoto]);

  // Cambiar facingMode (flip camera)
  const changeFacingMode = React.useCallback(() => {
    if (!ready) return;
    message.info("Cambiando modo de cámara...");
    setIsChangingCamera(true);

    setVideoConstraints((prev) => {
      const newFacingMode = prev.facingMode === "user" ? "environment" : "user";
      setMirror(newFacingMode === "user");
      return { ...prev, facingMode: newFacingMode };
    });

    setTimeout(() => setIsChangingCamera(false), 300);
  }, [ready]);

  // Manejar zoom
  const handleZoomChange = React.useCallback(
    async (increment: number) => {
      const track = getVideoTrack();
      if (!track) {
        message.error("No hay track de video disponible");
        return;
      }
      const capabilities = track.getCapabilities?.() as any;
      if (!capabilities || !capabilities.zoom) {
        message.error("Zoom no soportado en este dispositivo");
        return;
      }
      const settings = track.getSettings() as any;
      const newZoom = Math.min(
        capabilities.zoom.max,
        Math.max(capabilities.zoom.min, (settings.zoom || 0) + increment)
      );
      try {
        await track.applyConstraints({ advanced: [{ zoom: newZoom } as any] });
      } catch (err) {
        message.error("Error al aplicar zoom");
        console.error(err);
      }
    },
    [getVideoTrack]
  );

  const handleZoomIn = () => handleZoomChange(0.5);
  const handleZoomOut = () => handleZoomChange(-0.5);

  // Manejar toggle de torch
  const handleTorchToggle = React.useCallback(async () => {
    const track = getVideoTrack();
    if (!track) {
      message.error("No hay track de video");
      return;
    }

    // if (!torchSupported) {
    //   message.error(
    //     "El flash no es compatible en este dispositivo o navegador."
    //   );
    //   return;
    // }

    try {
      const newTorchState = !isTorchOn;
      await track.applyConstraints({
        advanced: [{ torch: newTorchState } as any],
      });
      setIsTorchOn(newTorchState);
      message.success(
        newTorchState ? "Linterna encendida" : "Linterna apagada"
      );
    } catch (err) {
      console.error("Error al intentar activar el flash:", err);
      message.error("Error al activar el flash.");
    }
  }, [getVideoTrack, isTorchOn, torchSupported]);

  // Fallback: usar cámara nativa
  const handleNativeCamera = React.useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.capture = "environment";
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            setFoto(reader.result as string);
            handleClose();
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, [setFoto, handleClose]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted={false}
      PaperProps={{
        style: {
          background: "rgba(39, 16, 128, 0.2)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        },
      }}
    >
      {/* Contenedor de la cámara */}
      <div style={styles.container}>
        {!isChangingCamera && (
          <Webcam
            ref={webcamRef}
            audio={false}
            mirrored={mirror}
            height="100%"
            width="100%"
            forceScreenshotSourceSize
            screenshotFormat="image/webp"
            videoConstraints={videoConstraints}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              margin: 0,
              padding: 0,
            }}
          />
        )}
      </div>

      {/* Botón cerrar */}
      <ButtonAntd
        style={styles.closeBtn}
        size="large"
        onClick={handleClose}
        type="primary"
        shape="circle"
        danger
        icon={<CloseCircleOutlined />}
      />

      {/* Cambiar facing mode (flip camera) */}
      <ButtonAntd
        style={styles.flipCameraBtn}
        size="large"
        disabled={numberOfCameras < 2 || !ready}
        onClick={changeFacingMode}
        type="dashed"
        shape="circle"
        icon={
          <InteractionTwoTone
            twoToneColor={numberOfCameras < 2 ? "#FFD700" : "#FF0000"}
          />
        }
      />

      {/* Encender / apagar torch o usar cámara nativa */}
      <ButtonAntd
        style={styles.torchBtn}
        size="large"
        onClick={handleTorchToggle}
        type="dashed"
        shape="circle"
        icon={<BulbTwoTone twoToneColor={isTorchOn ? "#FFD700" : "#FF0000"} />}
      />
      {upload ? (
        <ButtonAntd
          style={styles.nativeCameraBtn}
          size="large"
          onClick={handleNativeCamera}
          type="dashed"
          shape="circle"
          icon={<CameraOutlined />}
          title="Usar cámara nativa (flash disponible)"
        />
      ) : null}

      {/* Botón tomar foto */}
      <ConfigProvider theme={{ token: { colorPrimary: "#1E3A5F" } }}>
        <ButtonAntd
          style={styles.takePhotoBtn}
          loading={loading}
          disabled={loading || !ready}
          size="large"
          onClick={takeFoto}
          type="primary"
          shape="circle"
          icon={<CameraOutlined />}
        />
      </ConfigProvider>

      {/* Controles de zoom */}
      <div style={styles.zoomContainer}>
        <Space direction="vertical">
          <ButtonAntd
            size="large"
            onClick={handleZoomIn}
            type="primary"
            shape="circle"
            icon={<ZoomInOutlined />}
            disabled={!ready}
          />
          <ButtonAntd
            size="large"
            onClick={handleZoomOut}
            type="primary"
            shape="circle"
            icon={<ZoomOutOutlined />}
            disabled={!ready}
          />
        </Space>
      </div>
    </Dialog>
  );
};
